import {
  aspectLabel,
  FORMAT_PRESETS,
  getPreset,
  LIBRARY_TEMPLATES,
  type LibraryTemplate,
  libraryTemplateCategory,
  libraryTemplateSearchFields,
  PRESET_CATEGORY_LABELS,
  type PresetCategory,
  searchList,
} from "@cva/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { type FormEvent, useState } from "react";
import { toast } from "sonner";
import { orpc } from "../lib/api";
import { cn, errorMessage } from "../lib/utils";
import { TemplateTile } from "../routes/templates";
import { PresetIcon } from "./document-card";
import {
  LibraryTemplateTile,
  TemplateFormatField,
  useTemplateBrand,
  useTemplateDocument,
} from "./library-templates";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogFooter } from "./ui/dialog";
import { Field, Input } from "./ui/input";

const CATEGORIES: PresetCategory[] = ["social", "print", "reel"];
const SIZE_MIN = 50;
const SIZE_MAX = 10000;
const clampSize = (value: number) =>
  Math.round(Math.min(SIZE_MAX, Math.max(SIZE_MIN, Number.isFinite(value) ? value : SIZE_MIN)));

type Selection = { kind: "own"; id: string } | { kind: "library"; template: LibraryTemplate };

export function NewDocumentDialog({
  orgId,
  projectId,
  open,
  onOpenChange,
}: {
  orgId: string;
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [title, setTitle] = useState("");
  const [presetId, setPresetId] = useState("instagram-portrait");
  /** Own size instead of a ready-made format ("custom") and the board option. */
  const [customWidth, setCustomWidth] = useState(1080);
  const [customHeight, setCustomHeight] = useState(1350);
  const [board, setBoard] = useState(true);
  const [source, setSource] = useState<"format" | "template">("format");
  const [category, setCategory] = useState<PresetCategory | "all">("all");
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState<Selection | null>(null);
  /** Video format of a library video template. */
  const [templateFormat, setTemplateFormat] = useState<string | undefined>(undefined);
  const templates = useQuery({
    ...orpc.templates.list.queryOptions({ input: { orgId } }),
    enabled: open,
  });
  // Previews and the new file use the kit of this project.
  const brand = useTemplateBrand(orgId, projectId);
  const libraryTemplate = selection?.kind === "library" ? selection.template : null;
  const libraryContent = useTemplateDocument(libraryTemplate, brand, undefined, templateFormat);
  const own = searchList(
    (templates.data ?? []).filter(
      (template) => category === "all" || getPreset(template.formatPreset)?.category === category,
    ),
    query,
    (template) => [template.title, template.projectName, getPreset(template.formatPreset)?.label],
  );
  const library = searchList(
    LIBRARY_TEMPLATES.filter(
      (template) => category === "all" || libraryTemplateCategory(template) === category,
    ),
    query,
    libraryTemplateSearchFields,
  );
  const chosen = FORMAT_PRESETS.find((item) => item.id === presetId);
  /** Free sizes and the board exist for screen designs (not print, not video). */
  const boardPossible = presetId === "custom" || (chosen?.kind === "design" && !chosen.print);
  const create = useMutation(
    orpc.documents.create.mutationOptions({
      onSuccess: async (document) => {
        await queryClient.invalidateQueries({ queryKey: orpc.documents.key() });
        await queryClient.invalidateQueries({ queryKey: orpc.projects.key() });
        onOpenChange(false);
        setTitle("");
        await navigate({
          to: "/o/$orgId/documents/$documentId",
          params: { orgId, documentId: document.id },
        });
      },
      onError: (error) => toast.error(errorMessage(error)),
    }),
  );

  function submit(event: FormEvent) {
    event.preventDefault();
    if (source === "template") {
      if (selection?.kind === "own") {
        const template = templates.data?.find((item) => item.id === selection.id);
        if (!template) return;
        create.mutate({
          orgId,
          projectId,
          templateId: template.id,
          title: title.trim() || template.title,
        });
      } else if (selection?.kind === "library" && libraryContent) {
        create.mutate({
          orgId,
          projectId,
          content: libraryContent,
          title: title.trim() || selection.template.title,
        });
      }
      return;
    }
    const preset = FORMAT_PRESETS.find((item) => item.id === presetId);
    const own = presetId === "custom";
    const width = clampSize(customWidth);
    const height = clampSize(customHeight);
    create.mutate({
      orgId,
      projectId,
      presetId,
      ...(own ? { width, height } : {}),
      ...(boardPossible ? { board } : {}),
      title: title.trim() || (own ? `${width} × ${height} px` : preset?.label) || "Neue Datei",
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        title="Neue Datei"
        description={
          source === "format"
            ? "Wähle ein Format. Designs lassen sich später in ein anderes Format übertragen, Videos im Editor unter „Format ändern“ umstellen."
            : "Vorlagen übernehmen Farben, Schriften und Logo aus dem Brand Kit des Projekts."
        }
        className="max-w-3xl"
      >
        <form onSubmit={submit} className="space-y-5">
          <Field label="Titel">
            <Input
              autoFocus
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z. B. Herbstaktion Post 1"
            />
          </Field>
          <div className="inline-flex rounded-lg bg-zinc-100 p-0.5">
            {(
              [
                ["format", "Leeres Format"],
                ["template", "Aus Vorlage"],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => setSource(value)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-sm text-zinc-600",
                  source === value && "bg-white font-medium text-zinc-900 shadow-sm",
                )}
              >
                {label}
              </button>
            ))}
          </div>
          {source === "template" ? (
            <div className="space-y-3">
              <div className="flex flex-wrap items-center gap-1">
                {(["all", ...CATEGORIES] as const).map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    onClick={() => setCategory(entry)}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-xs font-medium text-zinc-600 hover:bg-zinc-100",
                      category === entry && "bg-zinc-900 text-white hover:bg-zinc-900",
                    )}
                  >
                    {entry === "all" ? "Alle" : PRESET_CATEGORY_LABELS[entry]}
                  </button>
                ))}
                <div className="relative ml-auto w-full sm:w-56">
                  <Search className="absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    type="search"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    // Enter searches, it does not create the file.
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.preventDefault();
                    }}
                    placeholder="Suchen, z. B. „Podcast“"
                    aria-label="Vorlagen durchsuchen"
                    className="h-8 pl-8 text-xs"
                  />
                </div>
              </div>
              {libraryTemplate ? (
                <TemplateFormatField
                  template={libraryTemplate}
                  value={templateFormat ?? libraryTemplate.presetId}
                  onChange={setTemplateFormat}
                />
              ) : null}
              <div className="max-h-[50vh] space-y-4 overflow-y-auto pr-1">
                {own.length > 0 ? (
                  <div>
                    <p className="mb-2 text-xs font-semibold text-zinc-700">Eigene Vorlagen</p>
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {own.map((template) => (
                        <TemplateTile
                          key={template.id}
                          template={template}
                          selected={selection?.kind === "own" && selection.id === template.id}
                          onClick={() => setSelection({ kind: "own", id: template.id })}
                        />
                      ))}
                    </div>
                  </div>
                ) : null}
                <div>
                  <p className="mb-2 text-xs font-semibold text-zinc-700">Vorlagen-Bibliothek</p>
                  {brand ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                      {library.map((template) => (
                        <LibraryTemplateTile
                          key={template.id}
                          template={template}
                          brand={brand}
                          selected={libraryTemplate?.id === template.id}
                          onClick={() => {
                            setSelection({ kind: "library", template });
                            setTemplateFormat(template.presetId);
                          }}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          ) : (
            [
              <div key="custom" className="space-y-3">
                <p className="text-xs font-semibold text-zinc-700">Eigene Größe</p>
                <fieldset
                  className={cn(
                    "m-0 flex min-w-0 flex-wrap items-center gap-3 rounded-lg border px-3 py-2.5 transition",
                    presetId === "custom"
                      ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                      : "border-zinc-200",
                  )}
                  onFocusCapture={() => setPresetId("custom")}
                >
                  <label className="flex items-center gap-1.5 text-sm">
                    <span className="text-xs text-zinc-500">Breite</span>
                    <Input
                      type="number"
                      min={SIZE_MIN}
                      max={SIZE_MAX}
                      value={customWidth}
                      onChange={(event) => setCustomWidth(Number(event.target.value))}
                      className="h-8 w-24"
                    />
                  </label>
                  <label className="flex items-center gap-1.5 text-sm">
                    <span className="text-xs text-zinc-500">Höhe</span>
                    <Input
                      type="number"
                      min={SIZE_MIN}
                      max={SIZE_MAX}
                      value={customHeight}
                      onChange={(event) => setCustomHeight(Number(event.target.value))}
                      className="h-8 w-24"
                    />
                  </label>
                  <span className="text-xs text-zinc-500">
                    px · beliebig, später pro Seite änderbar
                  </span>
                </fieldset>
                {boardPossible ? (
                  <label className="flex items-start gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={board}
                      onChange={(event) => setBoard(event.target.checked)}
                      className="mt-0.5 size-4 rounded border-zinc-300"
                    />
                    <span>
                      <span className="font-medium">Mehrere Seiten nebeneinander (Board)</span>
                      <span className="block text-xs text-zinc-500">
                        Alle Seiten liegen auf einer Fläche, jede mit eigener Größe – ideal für
                        App-Store-Screenshots oder Serien. Lässt sich im Editor jederzeit
                        umschalten.
                      </span>
                    </span>
                  </label>
                ) : null}
              </div>,
              ...CATEGORIES.map((entry) => (
                <div key={entry}>
                  <p className="mb-2 text-xs font-semibold text-zinc-700">
                    {PRESET_CATEGORY_LABELS[entry]}
                  </p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {FORMAT_PRESETS.filter((preset) => preset.category === entry).map((preset) => (
                      <button
                        key={preset.id}
                        type="button"
                        onClick={() => setPresetId(preset.id)}
                        title={preset.label}
                        className={cn(
                          "flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left transition",
                          presetId === preset.id
                            ? "border-brand-500 bg-brand-50 ring-2 ring-brand-500/20"
                            : "border-zinc-200 hover:border-zinc-300",
                        )}
                      >
                        <PresetIcon
                          presetId={preset.id}
                          className="size-4 shrink-0 text-zinc-500"
                        />
                        <span className="min-w-0">
                          <span className="block truncate text-sm font-medium">{preset.label}</span>
                          <span className="block text-xs text-zinc-500">
                            {preset.print
                              ? `${preset.print.widthMm} × ${preset.print.heightMm} mm`
                              : preset.kind === "video"
                                ? `${aspectLabel(preset.width, preset.height)} · ${preset.width} × ${preset.height} px`
                                : `${preset.width} × ${preset.height} px`}
                          </span>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )),
            ]
          )}
          <DialogFooter>
            <Button
              type="submit"
              loading={create.isPending}
              disabled={
                source === "template" &&
                (!selection || (selection.kind === "library" && !libraryContent))
              }
            >
              Datei anlegen
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
